var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { React, useEffect, useState } from 'libs/react';
import { Button } from 'components/Button';
import { Image } from 'components/Image';
import { Text } from 'components/Text';
import { FormGroup } from 'components/form/FormGroup';
import { FormField } from 'components/form/FormField';
import { FormInput } from 'components/form/FormInput';
import { Screen } from 'components/Screen';
import { MutationForm } from 'components/form/graphql/MutationForm';
import { SignupAccountMutation } from 'graphql/Account/mutations/SignupAccountMutation';
import { useAlert } from 'components/alert/AlertStack';
import { FormSection } from 'components/form/FormSection';
import { FormAction } from 'components/form/FormAction';
import { LoginFooter } from 'modules/auth/components/LoginFooter';
import { asset } from 'libs/asset';
import { useParams } from 'react-router-dom';
import i18n from 'components/translation/i18n';
import ReCAPTCHA from 'react-google-recaptcha';
import { UnexpectedError } from 'libs/error/UnexpectedError';
import { useSession } from 'components/session/SessionProvider';
import { useRoute } from 'components/router/Routing';
import './SignupScreen.scss';
/**
 * @component SignupScreen
 * @since 1.0.0
 */
export function SignupScreen(props) {
    const {} = props;
    const { lang } = useParams();
    const alert = useAlert();
    const { start, clear } = useSession();
    const route = useRoute();
    const [recaptchaSuccess, setRecaptchaSuccess] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');
    let language = lang !== null && lang !== void 0 ? lang : i18n.language.substring(0, 2);
    if (lang) {
        i18n.changeLanguage(lang);
        language = lang;
    }
    useEffect(() => {
        clear();
    }, []);
    useEffect(() => {
        document.body.classList.toggle('loaded', true);
    }, []);
    /**
     * @function onProcess
     * @since 1.0.0
     * @hidden
     */
    function onProcess(values) {
        const { name, firstname, lastname, email, password } = values;
        const input = {
            name,
            firstname,
            lastname,
            email,
            password,
            level: 'LEVEL_1',
            language
        };
        return {
            variables: {
                input: input
            }
        };
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onSuccess(result) {
        return __awaiter(this, void 0, void 0, function* () {
            alert.present({
                title: React.createElement("p", null, t('Account created! You will be redirected shortly.'))
            });
            const data = result.createAccount;
            if (data == null) {
                throw new UnexpectedError();
            }
            const startedAt = new Date(data.startedAt);
            const expiresAt = new Date(data.expiresAt);
            yield start(data.token, startedAt, expiresAt);
            setTimeout(() => {
                window.location.href = window.location.origin;
            }, 3000);
        });
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    function onFailure(error) {
        alert.present({ error });
    }
    /**
     * @function onRecaptchaSuccess
     * @since 1.0.0
     * @hidden
     */
    function onRecaptchaSuccess() {
        setRecaptchaSuccess(true);
    }
    /**
     * @function onRecaptchaExpired
     * @since 1.0.0
     * @hidden
     */
    function onRecaptchaExpired() {
        setRecaptchaSuccess(false);
    }
    /**
     * @function checkPasswordStrength
     * @since 1.0.0
     * @hidden
     */
    function checkPasswordStrength(value) {
        const regex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
        if (regex.test(value)) {
            setPasswordStrength('strong');
        }
        else {
            setPasswordStrength('weak');
        }
    }
    return (React.createElement(Screen, { title: "Signup", className: "signup-screen", ready: true, error: null },
        React.createElement(MutationForm, { name: "signup-form", id: "signup-form", mutation: SignupAccountMutation, onProcess: onProcess, onSuccess: onSuccess, onFailure: onFailure },
            React.createElement("div", { className: "signup-screen-logo" },
                React.createElement(Image, { source: asset('images/logo-white.svg') })),
            React.createElement("div", { className: "signup-screen-main" },
                React.createElement("div", { className: "signup-screen-head" },
                    React.createElement("h1", null,
                        React.createElement("strong", null, t('Reinvent')),
                        React.createElement("br", null),
                        t('your quotes')),
                    React.createElement(Text, { value: t('Enhance the customer experience and maximize your performance.') })),
                React.createElement("div", { className: "signup-screen-body" },
                    React.createElement("h2", null,
                        React.createElement("strong", null, t('Create your free account'))),
                    React.createElement(Text, { className: "signup-screen-body-title", value: t('30 days trial. No credit card required') }),
                    React.createElement(FormSection, null,
                        React.createElement(FormGroup, null,
                            React.createElement(FormField, null,
                                React.createElement(FormInput, { type: "text", name: "name", placeholder: t('Organization Name'), required: true }))),
                        React.createElement(FormGroup, null,
                            React.createElement(FormField, null,
                                React.createElement(FormInput, { type: "text", name: "firstname", placeholder: t('First Name'), required: true })),
                            React.createElement(FormField, null,
                                React.createElement(FormInput, { type: "text", name: "lastname", placeholder: t('Last Name'), required: true }))),
                        React.createElement(FormGroup, null,
                            React.createElement(FormField, null,
                                React.createElement(FormInput, { type: "email", name: "email", placeholder: t('Email'), required: true }))),
                        React.createElement(FormGroup, { className: "signup-screen-password-field" },
                            React.createElement(FormField, null,
                                React.createElement(FormInput, { type: "password", name: "password", placeholder: t('Password'), onChange: (value) => {
                                        checkPasswordStrength(value);
                                    }, required: true })),
                            passwordStrength === 'weak' && (React.createElement(Text, { className: "signup-screen-password-strength", value: t('Password must contain at least 8 characters, including uppercase, lowercase, and numbers.') })))),
                    React.createElement(FormAction, { align: "stretch" },
                        React.createElement("div", { className: "signup-screen-recaptcha" },
                            React.createElement(ReCAPTCHA, { sitekey: "6Le6Gy0qAAAAAJQBU_BcHycSNGZQS1rOrwTCFxRJ", onChange: onRecaptchaSuccess, onExpired: onRecaptchaExpired, hl: language })),
                        React.createElement(Button, { style: "primary", label: t('Create'), size: "large", submit: true, disabled: !recaptchaSuccess || passwordStrength === 'weak' })),
                    React.createElement("div", { className: "signup-screen-login" },
                        React.createElement(Text, { value: t('Already have an account?') }),
                        React.createElement("a", { href: "/", className: "signup-screen-login-link" }, t('Log in'))))),
            React.createElement(LoginFooter, { language: language, path: "signup" }))));
}
